import { useMutation } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';

import { MODAL_NAMES } from 'common/activeElements/modals/modalNames';
import { SIDEBAR_NAMES } from 'common/activeElements/sidebars/sidebarsNames';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { BATCH_WORK_ASSIGNMENTS_MAKE_ACTUAL_V2 } from 'gql';
import { useActivityMutations } from 'pages/WorkAssignments/components/WorkAssignmentActionMenu/useActivityMutation';
import { createAssignmentErrorNotification } from 'utils/assignmentCustomErrorGenerator';
import { formatHoursMinutesTime, getDateToEdit } from 'utils/datetime';

import { buildReconciliationTimeRangeInputs, toCrewMemberState } from './utils';

export const useReconciliation = ({
	assignment,
	plannedDeparture,
	plannedArrival,
	firstStation,
	lastStation,
	firstSequence,
	lastSequence,
	firstSequenceWorkAssignment,
	lastSequenceWorkAssignment,
	sequenceGroupId,
	departureTimezone,
	arrivalTimezone,
	onReconcileSuccess,
	onPopupClose,
	onShowRelatedAssignments,
	onManualReconciliationSuccess,
}) => {
	const [currentCrewMember, setCurrentCrewMember] = useState(
		toCrewMemberState(assignment?.crewMember)
	);
	const active = useActiveElements();

	useEffect(() => {
		setCurrentCrewMember(toCrewMemberState(assignment?.crewMember));
	}, [assignment?.crewMember]);

	const [updateBatchWorkAssignments, { loading: isLoading }] = useMutation(
		BATCH_WORK_ASSIGNMENTS_MAKE_ACTUAL_V2,
		{
			notifyOnNetworkStatusChange: true,
			onCompleted: (data) => {
				const payload = data?.batchWorkAssignmentsMakeActualV2;
				const type = payload?.__typename;

				if (type === 'WorkAssignmentBatchMutationSuccessResponseV2') {
					const sequence = payload?.workAssignmentSequences?.[0];

					notificationStore.setNotification({
						header: 'Success!',
						label: `Work assignment for ${sequence?.crewMember?.fullName ?? 'crew member'} for segment ${
							sequence?.fromStation?.code ?? firstStation?.code ?? 'N/A'
						} -> ${sequence?.toStation?.code ?? lastStation?.code ?? 'N/A'} was manually reconciled.`,
						appearance: 'success',
					});
					onPopupClose();
					onReconcileSuccess();
					return;
				}

				const errorList =
					payload?.errors?.flatMap((errorItem) => errorItem?.errors ?? []).filter(Boolean) ?? [];

				notificationStore.setNotification(
					createAssignmentErrorNotification(
						firstStation?.code ?? 'N/A',
						lastStation?.code ?? 'N/A',
						errorList
					)
				);
			},
			onError: () => {
				notificationStore.setNotification(standardError);
			},
		}
	);

	const handlePopupSubmit = async (formData) => {
		if (
			!firstStation?.id ||
			!lastStation?.id ||
			!sequenceGroupId ||
			!firstSequenceWorkAssignment?.id ||
			!firstSequenceWorkAssignment?.version ||
			!lastSequenceWorkAssignment?.id ||
			!lastSequenceWorkAssignment?.version
		) {
			notificationStore.setNotification(standardError);
			return;
		}

		const {
			arrivalTime,
			arrivalDate,
			departureDate,
			departureTime,
			note,
			newCrewMemberId,
			crewMember,
		} = formData;

		setCurrentCrewMember(crewMember ?? null);

		const timeRangeInputs = buildReconciliationTimeRangeInputs(
			{ departureDate, departureTime, arrivalDate, arrivalTime },
			{
				firstSequenceWorkAssignment,
				lastSequenceWorkAssignment,
				firstSequence,
				lastSequence,
				departureTimezone,
				arrivalTimezone,
			}
		);

		updateBatchWorkAssignments({
			variables: {
				input: {
					sequenceGroupId,
					fromStationId: firstStation.id,
					toStationId: lastStation.id,
					newCrewMemberId,
					activityType: 'driving',
					...(note ? { reason: note } : {}),
					timeRangeInputs,
				},
			},
		});
	};

	const popupDefaultValues = useMemo(() => {
		if (!plannedDeparture || !plannedArrival) {
			return {
				departureDate: '',
				departureTime: '',
				arrivalDate: '',
				arrivalTime: '',
				note: '',
				crewMember: currentCrewMember,
			};
		}

		return {
			departureDate: getDateToEdit(plannedDeparture, departureTimezone),
			departureTime: formatHoursMinutesTime(plannedDeparture, departureTimezone),
			arrivalDate: getDateToEdit(plannedArrival, arrivalTimezone),
			arrivalTime: formatHoursMinutesTime(plannedArrival, arrivalTimezone),
			note: '',
			crewMember: currentCrewMember,
		};
	}, [plannedDeparture, plannedArrival, currentCrewMember, departureTimezone, arrivalTimezone]);

	const buildJobReturnSelection = (payload, extra = {}) => ({
		...payload,
		sequenceGroupId,
		fromStationId: firstStation?.id ?? null,
		toStationId: lastStation?.id ?? null,
		fromStationName: firstStation?.code ?? firstStation?.name ?? 'N/A',
		toStationName: lastStation?.code ?? lastStation?.name ?? 'N/A',
		activityType: firstSequence?.activityType ?? 'driving',
		onAssignSuccess: onReconcileSuccess,
		...extra,
	});

	const { loading: isActionMenuLoading, ...actionCallbacks } = useActivityMutations({
		onManualReconciliationSuccess,
		onAssignDriverSuccess: (payload) => {
			active.setActiveElement(SIDEBAR_NAMES.assignJobReturn, {
				selection: buildJobReturnSelection(payload, { mode: 'assignOnly' }),
			});
		},
		onAssignJobReturnSuccess: (payload) => {
			active.setActiveElement(SIDEBAR_NAMES.assignJobReturn, {
				selection: buildJobReturnSelection(payload),
			});
		},
		onSplitAssignmentSuccess: () => active.setActiveElement(MODAL_NAMES.flow, { assignment }),
		onShowRelatedAssignmentsSuccess: onShowRelatedAssignments,
	});

	return {
		currentCrewMember,
		isLoading,
		popupDefaultValues,
		isActionMenuLoading,
		actionCallbacks,
		handlePopupSubmit,
	};
};
